import { motion } from "framer-motion";
import { CreditCard, Info } from "lucide-react";
import { clubs as defaultClubs } from "@/data/clubs";
import { Reveal } from "@/components/Reveal";

const cols = ["חוג", "תחום", "מדריך", "כיתות", "יום", "שעה", "מחיר לחודש", "הרשמה"];

export const ScheduleTable = ({ clubs = defaultClubs, regUrl = "" }) => {
  const rows = clubs.flatMap((c) =>
    c.schedule.length === 0
      ? [{
          club: c.name,
          field: c.field,
          instructor: c.instructor,
          grades: "א'-ו'",
          day: c.scheduleNote || "פרטים בהמשך",
          time: "—",
          price: c.price,
        }]
      : c.schedule.map((s) => ({
          club: c.name,
          field: c.field,
          instructor: c.instructor,
          grades: s.grades,
          day: s.day,
          time: s.time,
          price: s.price,
        }))
  );
  return (
    <section id="schedule" className="px-5 md:px-10 py-20 md:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="font-head font-semibold text-accent mb-3">כל הפרטים במקום אחד</p>
          <h2 className="font-head font-black text-4xl md:text-6xl tracking-tighter leading-none mb-6">
            שעות ועלויות
          </h2>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="flex flex-col sm:flex-row gap-4 mb-10 md:mb-14" data-testid="payment-notes">
            <div className="flex items-start gap-3 rounded-xl border border-ink/10 bg-white/60 p-4 flex-1">
              <CreditCard className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <p className="text-ink/70 leading-relaxed">
                התשלום מתבצע דרך מערכת ההרשמה לחוגים של המועצה.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-ink/10 bg-white/60 p-4 flex-1">
              <Info className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <p className="text-ink/70 leading-relaxed">
                עלות דמי רישום חד־פעמית — תתווסף למחיר החוג.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-x-auto rounded-2xl border border-ink/10 bg-white/50" data-testid="schedule-table">
            <table className="w-full min-w-[760px] text-right border-collapse">
              <thead>
                <tr className="bg-ink text-paper">
                  {cols.map((c) => (
                    <th key={c} className="font-head font-semibold text-sm md:text-base p-4 md:p-5 whitespace-nowrap">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.4) }}
                    className={`${i % 2 ? "bg-transparent" : "bg-ink/[0.03]"} hover:bg-accent/5 transition-colors`}
                  >
                    <td className="p-4 md:p-5 font-head font-bold text-base md:text-lg">{r.club}</td>
                    <td className="p-4 md:p-5 text-ink/60 text-sm whitespace-nowrap">{r.field}</td>
                    <td className="p-4 md:p-5 text-ink/70 text-sm">{r.instructor}</td>
                    <td className="p-4 md:p-5 whitespace-nowrap">{r.grades}</td>
                    <td className="p-4 md:p-5 whitespace-nowrap">{r.day}</td>
                    <td className="p-4 md:p-5 whitespace-nowrap tabular-nums" dir="ltr">{r.time}</td>
                    <td className="p-4 md:p-5 whitespace-nowrap">
                      <span className="font-head font-bold text-accent">{r.price} ₪</span>
                    </td>
                    <td className="p-4 md:p-5 whitespace-nowrap">
                      {regUrl && (
                        <a
                          href={regUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex font-head font-semibold text-xs bg-accent text-white px-3.5 py-1.5 rounded-full hover:bg-accent-600 transition-colors"
                          data-testid={`schedule-register-${i}`}
                        >
                          הרשמה ←
                        </a>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
